<template>
  <el-drawer
    v-model="visible"
    title="도움말"
    direction="rtl"
    :size="400"
    :destroy-on-close="false"
  >
    <el-tabs v-model="activeTab" class="help-tabs">
      <!-- 화면 가이드 탭 -->
      <el-tab-pane label="화면 가이드" name="screen">
        <HelpContent :content-path="contentPath" />
        <!-- 마지막 검증 (테스트 관리 qa 연계, 2026-10-18) — 이 설명이 실제로 확인된 것인지 -->
        <div v-if="verified && (verified.last_pass_at || verified.scenarios_passed)" class="verified-line">
          ✔ 마지막 검증: {{ verified.run }} · {{ (verified.last_pass_at || '').slice(0, 10) }} 통과 ({{ verified.by }}) · 시나리오 {{ verified.scenarios_passed }}개
        </div>
        <div v-else-if="verified" class="verified-line none">이 화면은 아직 테스트 회차에서 검증된 기록이 없습니다.</div>
      </el-tab-pane>

      <!-- 테스트 탭 — 진행 중 회차에 내 배정이 있을 때만 (2026-10-18) -->
      <el-tab-pane v-if="qa.hasRun" name="test">
        <template #label>
          <span>테스트 <el-badge v-if="qa.herePending" :value="qa.herePending" class="tab-badge" /></span>
        </template>
        <TestPanelContent />
      </el-tab-pane>

      <!-- 업무 흐름 탭 -->
      <el-tab-pane label="업무 흐름" name="workflow">
        <!-- 업무 흐름 지도 (DFD) 연계 (2026-07-15) — /help/dfd/ 정적 배포본 -->
        <div class="dfd-link">
          <el-button type="primary" plain size="small" @click="openDfdMap">
            📊 업무 흐름 지도(DFD) 전체 보기
          </el-button>
          <div class="dfd-hint">
            디자인·견적 → 생산 → 자재 → 물류 → 무역 → 원가·분석 —
            영역별 상세 흐름도와 메뉴 대응표
          </div>
        </div>
        <el-divider style="margin: 12px 0" />
        <HelpContent content-path="workflow/overview" />
      </el-tab-pane>

      <!-- FAQ 탭 -->
      <el-tab-pane label="FAQ" name="faq">
        <template v-if="verified && verified.tips && verified.tips.length">
          <div class="tips-title">이 화면에서 자주 막히는 곳 (테스트 시나리오)</div>
          <div v-for="t in verified.tips" :key="t.step_no" class="tips-item"><b>{{ t.step_no }} {{ t.title }}</b> — {{ t.tips }}</div>
          <el-divider style="margin: 10px 0" />
        </template>
        <el-collapse accordion>
          <el-collapse-item
            v-for="(faq, index) in faqs"
            :key="index"
            :title="faq.question"
          >
            <!-- eslint-disable-next-line vue/no-v-html -- DOMPurify로 새니타이징된 값만 바인딩됨 (utils/sanitizeHtml.js) -->
            <div v-html="sanitizeHtml(faq.answer)"></div>
          </el-collapse-item>
        </el-collapse>
      </el-tab-pane>
    </el-tabs>
  </el-drawer>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import HelpContent from './HelpContent.vue'
import TestPanelContent from '@/components/qa/TestPanelContent.vue'
import { useQaStore } from '@/stores/qa'
import axiosInstance from '@/api/axios'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  contentPath: {
    type: String,
    default: 'screens/default'
  }
})

const emit = defineEmits(['update:modelValue'])

const visible = ref(props.modelValue)
const activeTab = ref('screen')
const qa = useQaStore()
const route = useRoute()
const verified = ref(null)

async function loadVerified() {
  try {
    const res = await axiosInstance.get('qa/last-verified/', { params: { path: route.path } })
    verified.value = res.data || {}
  } catch (e) { verified.value = null }
}

// 업무 흐름 지도(DFD) — 빌드에 포함되는 정적 파일 (client/public/help/dfd/)
const openDfdMap = () => {
  window.open('/help/dfd/dfd-index.html', '_blank')
}

const faqs = ref([
  {
    question: '메인오더은 어떻게 생성하나요?',
    answer: '메인오더 목록 화면에서 <strong>신규 요청</strong> 버튼을 클릭하여 생성할 수 있습니다. 필수 항목(브랜드, 디자인, 발주수량, 납기일)을 입력 후 저장하세요.'
  },
  {
    question: '원가 정보가 보이지 않아요.',
    answer: '원가 정보는 <strong>원가조회자</strong> 또는 <strong>원가관리자</strong> 권한이 있는 사용자만 볼 수 있습니다. 권한이 필요하면 관리자에게 문의하세요.'
  },
  {
    question: 'LC 상태는 어떻게 변경하나요?',
    answer: 'LC 상세 화면에서 <strong>상태변경</strong> 드롭다운 버튼을 클릭하여 다음 단계로 진행할 수 있습니다. LC관리자 권한이 필요합니다.'
  },
  {
    question: '마일스톤 알림은 어떻게 설정하나요?',
    answer: '마일스톤은 예정일 N일 전에 자동으로 알림이 발송됩니다. 알림 설정은 마일스톤 생성/수정 시 변경할 수 있습니다.'
  },
  {
    question: 'LOT 배분은 어떻게 하나요?',
    answer: '자재 LOT 목록에서 잔여수량이 있는 LOT의 <strong>배분</strong> 버튼을 클릭하여 특정 메인오더에 수량을 배분할 수 있습니다.'
  }
])

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    qa.fetch(route.path)
    loadVerified()
    // 배정이 있으면 테스트 탭을 먼저 — 담당자는 가이드를 보러 연 것
    if (qa.hasRun && qa.herePending) activeTab.value = 'test'
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})
</script>

<style scoped>
.dfd-link { padding: 2px 4px 0; }
.verified-line { margin: 10px 4px 0; padding: 6px 8px; background: #f0f9eb; color: #3c763d; border-radius: 4px; font-size: 12px; }
.verified-line.none { background: #f4f4f5; color: #909399; }
.tab-badge :deep(.el-badge__content) { transform: translateY(-2px); }
.tips-title { font-weight: 600; font-size: 13px; margin: 2px 0 6px; }
.tips-item { font-size: 12.5px; line-height: 1.5; margin: 4px 0; padding: 4px 6px; background: #fdf6ec; border-radius: 4px; }
.dfd-hint { font-size: 12px; color: #909399; margin-top: 6px; line-height: 1.5; }
.help-tabs {
  height: 100%;
}

.help-tabs :deep(.el-tabs__content) {
  padding: 10px;
}

.el-collapse-item {
  margin-bottom: 5px;
}

.el-collapse-item :deep(.el-collapse-item__header) {
  font-weight: 500;
}
</style>
